/**
 * EducationStandardsManager — admin editor for the tier-scoped
 * "Standards of Education" that guide every AI response path and the
 * lesson-plan builder.
 *
 * Free / Pro / Business / Enterprise each get one editable card with:
 *   - Learning objectives (list)
 *   - Tone guidance (text)
 *   - Depth requirements (text)
 *   - Content DO (list)
 *   - Content DON'T (list)
 *
 * List fields are edited as newline-separated textareas — it's the
 * lowest-friction way to add/remove items without a full drag-and-drop
 * builder, and matches how the underlying prompt is rendered.
 */
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { RefreshCw, Save, RotateCcw } from 'lucide-react';

const TIERS = ['free', 'pro', 'business', 'enterprise'];

const TIER_LABEL = { free: 'Free', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' };

const linesToArr = (s) => (s || '').split('\n').map((l) => l.trim()).filter(Boolean);
const arrToLines = (a) => (Array.isArray(a) ? a : []).join('\n');

export default function EducationStandardsManager() {
  const { API } = useContext(AuthContext);
  const [standards, setStandards] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(null);
  const [activeTier, setActiveTier] = useState('free');

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/education-standards`);
      const map = {};
      (res.data || []).forEach((s) => {
        map[s.tier] = {
          ...s,
          _learning_objectives_txt: arrToLines(s.learning_objectives),
          _content_do_txt:          arrToLines(s.content_do),
          _content_dont_txt:        arrToLines(s.content_dont),
        };
      });
      setStandards(map);
    } catch {
      toast.error('Failed to load education standards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const patch = (tier, field, value) => {
    setStandards((s) => ({ ...s, [tier]: { ...s[tier], [field]: value } }));
  };

  const save = async (tier) => {
    const s = standards[tier];
    setSavingTier(tier);
    try {
      const payload = {
        learning_objectives: linesToArr(s._learning_objectives_txt),
        tone_guidance:       s.tone_guidance || '',
        depth_requirements:  s.depth_requirements || '',
        content_do:          linesToArr(s._content_do_txt),
        content_dont:        linesToArr(s._content_dont_txt),
      };
      await axios.put(`${API}/admin/education-standards/${tier}`, payload);
      toast.success(`${TIER_LABEL[tier]} standard saved — will apply to next AI response`);
      await loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(String(msg));
    } finally {
      setSavingTier(null);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset ALL four tiers to factory defaults? This overwrites any custom edits.')) return;
    try {
      await axios.post(`${API}/admin/education-standards/reset-defaults`);
      toast.success('Defaults restored');
      await loadAll();
    } catch {
      toast.error('Reset failed');
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-500 py-6 text-center">Loading standards…</p>;
  }

  return (
    <div className="space-y-4" data-testid="education-standards-manager">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Standards of Education</CardTitle>
              <CardDescription>
                Per-tier guardrails that steer every AI answer (Ask FreedrIQ, Ask an Expert, main chat)
                AND the lesson-plan builder. Free stays introductory, Enterprise gets compliance depth.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadAll} data-testid="standards-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={resetDefaults} data-testid="standards-reset-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset defaults
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={activeTier} onValueChange={setActiveTier}>
        <TabsList className="grid grid-cols-4 w-full">
          {TIERS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`standard-tier-tab-${t}`}>
              {TIER_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>

        {TIERS.map((t) => {
          const s = standards[t] || {};
          return (
            <TabsContent key={t} value={t}>
              <Card>
                <CardHeader>
                  <CardTitle>{TIER_LABEL[t]} tier</CardTitle>
                  <CardDescription>
                    Last edited: {s.updated_at ? new Date(s.updated_at).toLocaleString() : 'never (factory defaults)'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Learning objectives (one per line)</Label>
                    <Textarea
                      rows={4}
                      value={s._learning_objectives_txt || ''}
                      onChange={(e) => patch(t, '_learning_objectives_txt', e.target.value)}
                      data-testid={`standard-objectives-${t}`}
                    />
                  </div>
                  <div>
                    <Label>Tone guidance</Label>
                    <Textarea
                      rows={2}
                      value={s.tone_guidance || ''}
                      onChange={(e) => patch(t, 'tone_guidance', e.target.value)}
                      data-testid={`standard-tone-${t}`}
                    />
                  </div>
                  <div>
                    <Label>Depth requirements</Label>
                    <Textarea
                      rows={3}
                      value={s.depth_requirements || ''}
                      onChange={(e) => patch(t, 'depth_requirements', e.target.value)}
                      data-testid={`standard-depth-${t}`}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>DO (one per line)</Label>
                      <Textarea
                        rows={4}
                        value={s._content_do_txt || ''}
                        onChange={(e) => patch(t, '_content_do_txt', e.target.value)}
                        data-testid={`standard-do-${t}`}
                      />
                    </div>
                    <div>
                      <Label>DON&apos;T (one per line)</Label>
                      <Textarea
                        rows={4}
                        value={s._content_dont_txt || ''}
                        onChange={(e) => patch(t, '_content_dont_txt', e.target.value)}
                        data-testid={`standard-dont-${t}`}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={() => save(t)}
                      disabled={savingTier === t}
                      data-testid={`standard-save-${t}`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingTier === t ? 'Saving…' : `Save ${TIER_LABEL[t]}`}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
